import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  postNotificationsUserNotificationsUserNotificationPublicIdSeen,
  type PagedNotificationResult,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { notificationsKeys } from "Api/Queries/Notifications/useNotifications";
import { newNotificationsCountKeys } from "Api/Queries/Notifications/useNewNotificationsCount";

export const useMarkNotificationAsSeen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      (userNotificationPublicID: string) =>
        postNotificationsUserNotificationsUserNotificationPublicIdSeen(
          userNotificationPublicID,
        ),
    ),
    onMutate: async (publicId: string) => {
      await queryClient.cancelQueries({
        queryKey: notificationsKeys.all,
      });

      const previousNotifications = queryClient.getQueriesData({
        queryKey: notificationsKeys.all,
      });

      // Capture current unread count before decrementing
      const previousCount = queryClient.getQueryData<{ count: number }>(
        newNotificationsCountKeys.all,
      );

      // Optimistically update all notification lists (including paged keys)
      queryClient.setQueriesData<PagedNotificationResult>(
        { queryKey: notificationsKeys.all },
        (old) => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            items: old.items.map((n) =>
              n.publicID === publicId
                ? { ...n, seenAt: new Date().toISOString() }
                : n,
            ),
          };
        },
      );

      // Decrement unread count
      queryClient.setQueryData<{ count: number }>(
        newNotificationsCountKeys.all,
        (old) => ({
          count: Math.max(0, (old?.count || 0) - 1),
        }),
      );

      return { previousNotifications, previousCount };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        context.previousNotifications.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      // Restore previous unread count
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          newNotificationsCountKeys.all,
          context.previousCount,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: notificationsKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: newNotificationsCountKeys.all,
      });
    },
  });
};
